import { useMemo, useState } from "react";
import type { ModelTemplate } from "@/modules/plus/plus.schema";
import { TemplatePhotoGallery } from "./TemplatePhotoGallery";
import { composeCode, defaultForField, ValuePicker } from "./ValuePicker";

// Template-mode content. The sheet header renders the model photo/title
// already; this just shows the catalog metadata + the inline code builder
// + accessories + notes.
//
// Owns the ValuePicker selection so TemplatePhotoGallery can prefill the
// upload dialog with the current composed code.
export const ModelDetail: React.FC<{ template: ModelTemplate }> = ({ template }) => {
  const initial = useMemo(() => {
    const init: Record<number, string> = {};
    template.fields.forEach((f, i) => {
      init[i] = defaultForField(f, i === 0, template.series_id);
    });
    return init;
  }, [template]);

  const [values, setValues] = useState<Record<number, string>>(initial);
  const composedCode = composeCode(template, values);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-0.5 text-xs text-gray-500 dark:text-gray-400">
        <span className="font-mono">Example: {template.example}</span>
        {template.catalog_page && <span>Catalog page: {template.catalog_page}</span>}
      </div>

      <ValuePicker
        template={template}
        values={values}
        onChange={setValues}
        onReset={() => setValues(initial)}
      />

      <TemplatePhotoGallery template={template} composedCode={composedCode} />

      {template.accessories && template.accessories.length > 0 && (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Accessories ({template.accessories.length})
          </h2>
          <ul className="mt-2 space-y-1.5">
            {template.accessories.map((a) => (
              <li key={a.code} className="text-sm">
                <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs dark:bg-gray-800 dark:text-gray-200">
                  {a.code}
                </code>{" "}
                <span className="text-gray-800 dark:text-gray-200">{a.meaning}</span>{" "}
                <span className="text-xs text-gray-500 dark:text-gray-400">— {a.category}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {template.notes && template.notes.length > 0 && (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Notes
          </h2>
          <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-gray-700 dark:text-gray-300">
            {template.notes.map((n, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: notes are plain strings that may repeat
              <li key={i}>{n}</li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
};
